/**
 * HeroAreaChart — Dual-layer monotone cubic spline SVG chart for the hero gradient.
 *
 * Two variants: ArtChart (earned + spend) and SurvivalChart (earned + burn).
 * Both render a dark back layer (spend/burn, full month) and a white front layer
 * (earned, sliced to today) with a scrubber dot on the last earned point.
 *
 * Edge-to-edge: no horizontal padding. viewBox 393×75.
 * Uses Monotone Cubic Hermite interpolation for smooth curves that don't overshoot.
 *
 * Fill treatment:
 * - Earned: solid lighter teal (#68CFC7) extending well below the chart
 *   so it bleeds into the hero's padding zone (behind the Net card overlap).
 * - Spend/Burn: dark gradient fill, also extended, visible right of the
 *   earned scrubber dot where earned hasn't painted over it yet.
 */

const W = 393;
const H = 75;
/** How far below the chart baseline (H) the area fills extend.
 *  Ensures teal pool bleeds through the hero's 36px bottom padding
 *  and behind the overlapping Net card. */
const FILL_BOTTOM = H + 200;

/** Small x-offset so Day 1 scrubber dot isn't clipped at left edge */
const DOT_X_OFFSET = 6;

/** Shared Y position for $0 on Day 1 — both Art and Survival use the same
 *  baseline so toggling tabs doesn't shift the dot vertically. */
const DAY1_ZERO_Y = H - 8;

// ── Monotone Cubic Hermite Spline ──

function monotonePath(pts: [number, number][]): string {
  if (pts.length < 2) return '';
  if (pts.length === 2) return `M ${pts[0][0]},${pts[0][1]} L ${pts[1][0]},${pts[1][1]}`;

  const n = pts.length;
  const dx: number[] = [];
  const dy: number[] = [];
  const m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = pts[i + 1][0] - pts[i][0];
    dy[i] = pts[i + 1][1] - pts[i][1];
    m[i] = dx[i] === 0 ? 0 : dy[i] / dx[i];
  }
  const t: number[] = [m[0]];
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) t[i] = 0;
    else t[i] = (m[i - 1] + m[i]) / 2;
  }
  t[n - 1] = m[n - 2];

  for (let i = 0; i < n - 1; i++) {
    if (Math.abs(m[i]) < 1e-6) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i] / m[i];
    const b = t[i + 1] / m[i];
    const s = a * a + b * b;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      t[i] = tau * a * m[i];
      t[i + 1] = tau * b * m[i];
    }
  }

  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    const seg = dx[i] / 3;
    d += ` C ${p0[0] + seg},${p0[1] + t[i] * seg} ${p1[0] - seg},${p1[1] - t[i + 1] * seg} ${p1[0]},${p1[1]}`;
  }
  return d;
}

function smoothArea(pts: [number, number][], h: number): string {
  const line = monotonePath(pts);
  if (!line) return '';
  return line + ` L ${pts[pts.length - 1][0]},${h} L ${pts[0][0]},${h} Z`;
}

// ── Art Chart ──

interface ArtChartProps {
  earnData: number[];   // cumulative earned per day (dollars), length = daysInMonth
  spendData: number[];  // cumulative spend per day (dollars), length = daysInMonth
  dayOfMonth: number;   // 1-based current day
}

export function ArtChart({ earnData, spendData, dayOfMonth }: ArtChartProps) {
  // Prepend zero so line starts at month origin
  const eRaw = [0, ...earnData];
  const sRaw = [0, ...spendData];
  const totalDays = eRaw.length;
  if (totalDays < 2) return null;

  const eSlice = eRaw.slice(0, dayOfMonth + 1);
  const all = [...sRaw, ...eSlice];
  const min = Math.min(...all, 0) - 20;
  const max = Math.max(...all) + 20;
  const range = max - min || 1;
  const yv = (v: number) => H - ((v - min) / range) * H;
  const xv = (i: number) => (i / (totalDays - 1)) * W;

  const sPts: [number, number][] = sRaw.map((v, i) => [xv(i), yv(v)]);
  const ePts: [number, number][] = eSlice.map((v, i) => [xv(i), yv(v)]);

  // Day 1: only 1 earned point — render back layer + scrubber dot (no earned curve)
  if (ePts.length < 2) {
    const flatPts: [number, number][] = spendData.map((_, i) => [xv(i), DAY1_ZERO_Y]);
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', overflow: 'visible' }} preserveAspectRatio="none">
        <line x1={0} y1={H} x2={W} y2={H} stroke="rgba(255,255,255,0.25)" strokeWidth={1} />
        <circle cx={DOT_X_OFFSET} cy={DAY1_ZERO_Y} r={4.5} fill="white" />
        <circle cx={DOT_X_OFFSET} cy={DAY1_ZERO_Y} r={8} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={3} />
      </svg>
    );
  }

  const lx = ePts[ePts.length - 1][0];
  const ly = ePts[ePts.length - 1][1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', overflow: 'visible' }} preserveAspectRatio="none">
      <defs>
        <linearGradient id="artSpendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,0,0,0.22)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.05)" />
        </linearGradient>
      </defs>
      {/* Earned area fill — background pool */}
      <path d={smoothArea(ePts, FILL_BOTTOM)} fill="#68CFC7" />
      {/* Spend area + stroke — dark gradient, visible on top of earned fill */}
      <path d={smoothArea(sPts, FILL_BOTTOM)} fill="url(#artSpendFill)" />
      <path d={monotonePath(sPts)} fill="none" stroke="rgba(0,0,0,0.22)" strokeWidth="2.5" />
      {/* Earned stroke — white line on top */}
      <path d={monotonePath(ePts)} fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="3" />
      {/* Scrubber dot */}
      <circle cx={lx} cy={ly} r={4.5} fill="white" />
      <circle cx={lx} cy={ly} r={8} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={3} />
    </svg>
  );
}

// ── Survival Chart ──

interface SurvivalChartProps {
  earnData: number[];   // cumulative earned per day (dollars), length = daysInMonth
  burnLine: number[];   // cumulative burn per day (dollars), length = daysInMonth
  dayOfMonth: number;
}

export function SurvivalChart({ earnData, burnLine, dayOfMonth }: SurvivalChartProps) {
  // Prepend zero so line starts at month origin
  const eRaw = [0, ...earnData];
  const bRaw = [0, ...burnLine];
  const totalDays = eRaw.length;
  if (totalDays < 2) return null;

  // Use full arrays for Y scale so it stays stable
  const maxVal = Math.max(...bRaw, ...eRaw) + 40;
  const yv = (v: number) => H - (v / (maxVal || 1)) * H;
  const xv = (i: number) => (i / (totalDays - 1)) * W;

  const earnSlice = eRaw.slice(0, dayOfMonth + 1);
  const ePts: [number, number][] = earnSlice.map((v, i) => [xv(i), yv(v)]);
  const bPts: [number, number][] = bRaw.map((v, i) => [xv(i), yv(v)]);

  // Day 1: only 1 earned point — render burn back layer + scrubber dot (no earned curve)
  // Use DAY1_ZERO_Y as the $0 baseline so both Art and Survival dots are pixel-identical.
  // When burn has a ramp, rescale so the line starts from DAY1_ZERO_Y (not H).
  if (ePts.length < 2) {
    const burnHasData = burnLine.some((v) => v > 0);
    let day1Pts: [number, number][];

    if (!burnHasData) {
      // All-zero burn — flat line at shared zero baseline
      day1Pts = burnLine.map((_, i) => [xv(i), DAY1_ZERO_Y] as [number, number]);
    } else {
      // Burn ramp present — rescale so $0 maps to DAY1_ZERO_Y (not H)
      const day1Yv = (v: number) => DAY1_ZERO_Y - (v / (maxVal || 1)) * DAY1_ZERO_Y;
      day1Pts = burnLine.map((v, i) => [xv(i), day1Yv(v)] as [number, number]);
    }

    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', overflow: 'visible' }} preserveAspectRatio="none">
        <defs>
          <linearGradient id="survBurnFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(0,0,0,0.22)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.05)" />
          </linearGradient>
        </defs>
        <path d={smoothArea(day1Pts, FILL_BOTTOM)} fill="url(#survBurnFill)" />
        <path d={monotonePath(day1Pts)} fill="none" stroke="rgba(0,0,0,0.22)" strokeWidth="2.5" />
        <circle cx={DOT_X_OFFSET} cy={DAY1_ZERO_Y} r={4.5} fill="white" />
        <circle cx={DOT_X_OFFSET} cy={DAY1_ZERO_Y} r={8} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={3} />
      </svg>
    );
  }

  const lx = ePts[ePts.length - 1][0];
  const ly = ePts[ePts.length - 1][1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', overflow: 'visible' }} preserveAspectRatio="none">
      <defs>
        <linearGradient id="survBurnFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,0,0,0.22)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.05)" />
        </linearGradient>
      </defs>
      {/* Earned area fill — background pool */}
      <path d={smoothArea(ePts, FILL_BOTTOM)} fill="#68CFC7" />
      {/* Burn area + stroke — dark gradient, visible on top of earned fill */}
      <path d={smoothArea(bPts, FILL_BOTTOM)} fill="url(#survBurnFill)" />
      <path d={monotonePath(bPts)} fill="none" stroke="rgba(0,0,0,0.22)" strokeWidth="2.5" />
      {/* Earned stroke — white line on top */}
      <path d={monotonePath(ePts)} fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="3" />
      {/* Scrubber dot */}
      <circle cx={lx} cy={ly} r={4.5} fill="white" />
      <circle cx={lx} cy={ly} r={8} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={3} />
    </svg>
  );
}
